import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Shield, CheckCircle2, AlertTriangle, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function FsePanel() {
  const [status, setStatus] = useState(null);
  const [accept, setAccept] = useState({ items: [], current_version: "" });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [s, a] = await Promise.all([
        api.get("/admin/fse/status"),
        api.get("/admin/medico/responsabilita-status"),
      ]);
      setStatus(s.data);
      setAccept(a.data);
    } catch (e) {
      toast.error(apiErr(e, "Errore caricamento stato FSE"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading || !status) return <div className="text-sm text-slate-500">Caricamento…</div>;

  const Row = ({ label, value, ok }) => (
    <div className="flex items-center justify-between border-b border-[#E2E8F0] py-2 text-sm">
      <span className="text-slate-600">{label}</span>
      <span className="flex items-center gap-1.5 font-bold">
        {ok === true && <CheckCircle2 size={13} className="text-emerald-600" />}
        {ok === false && <AlertTriangle size={13} className="text-amber-500" />}
        <span>{String(value || "—")}</span>
      </span>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="admin-fse-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Shield size={18} className="text-brand-azure" />
          <h2 className="font-extrabold text-brand-navy text-xl">FSE 2.0 — Ready locale</h2>
          <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">{status.status_label}</span>
        </div>
        <button onClick={load} className="text-sm font-bold text-brand-navy hover:bg-slate-100 px-3 py-1.5 rounded-lg flex items-center gap-1.5" data-testid="fse-refresh">
          <RefreshCw size={13} /> Aggiorna
        </button>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-xs text-amber-900">
        <b>Banner:</b> {status.banner}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-4">
          <h3 className="font-extrabold text-brand-navy text-sm mb-2">Funzionalità modulo</h3>
          <Row label="Modulo FSE-ready" value={status.fse_ready_module_enabled ? "attivo" : "disattivo"} ok={status.fse_ready_module_enabled} />
          <Row label="Generazione CDA R2" value={status.cda_generation_enabled ? "attiva" : "no"} ok={status.cda_generation_enabled} />
          <Row label="PDF/A status" value={status.pdfa_status_supported ? "supportato" : "no"} ok={status.pdfa_status_supported} />
          <Row label="Upload PAdES firmato" value={status.pades_upload_supported ? "supportato" : "no"} ok={status.pades_upload_supported} />
          <Row label="Tipo documento default" value={status.document_type_default} />
          <Row label="Ultima preparazione" value={status.last_package_generated_at ? new Date(status.last_package_generated_at).toLocaleString("it-IT") : "—"} />
          <Row label="Referti pubblicati" value={status.published_count} />
          <Row label="Pacchetti con errori" value={status.validation_errors_count} ok={status.validation_errors_count === 0} />
        </div>

        <div className="bg-white border border-[#E2E8F0] rounded-xl p-4">
          <h3 className="font-extrabold text-brand-navy text-sm mb-2">Configurazione Gateway</h3>
          <Row label="Modalità" value={status.gateway_mode} ok={status.gateway_configured || status.provider_configured} />
          <Row label="Gateway base URL" value={status.gateway_base_url || "non configurato"} ok={!!status.gateway_base_url} />
          <Row label="Cert. autenticazione" value={status.auth_cert_status} ok={status.auth_cert_status === "configurato"} />
          <Row label="Cert. firma" value={status.sign_cert_status} ok={status.sign_cert_status === "configurato"} />
          <Row label="OID organizzazione" value={status.organization_oid || "—"} ok={!!status.organization_oid} />
          <Row label="Codice struttura" value={status.structure_code || "—"} ok={!!status.structure_code} />
          <Row label="Codice regione" value={status.region_code || "—"} ok={!!status.region_code} />
          <div className="mt-3 text-[11px] text-slate-500 italic">
            Per attivare l'invio al Gateway servono certificati di autenticazione e firma, oltre alla configurazione OID/struttura. Nessun dato reale viene inviato finché tutti i campi non sono configurati e <code>FSE_PROVIDER_MODE=gateway_test</code> o <code>gateway_production</code>.
          </div>
        </div>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-xl p-4">
        <h3 className="font-extrabold text-brand-navy text-sm mb-2">Accesso responsabile medico</h3>
        <div className="text-xs text-slate-600 mb-2">Versione testo corrente: <b>{accept.current_version}</b>. Quando il testo cambia versione i medici devono accettarlo nuovamente al login.</div>
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-[#E2E8F0]">
            <tr><th className="text-left py-1.5 px-2">Medico</th><th className="text-left">Email</th><th className="text-left">Versione accettata</th><th className="text-left">Accettato il</th><th className="text-left">Stato</th></tr>
          </thead>
          <tbody>
            {accept.items.map((m) => (
              <tr key={m.doctor_id} className="border-b border-[#E2E8F0]">
                <td className="py-1.5 px-2 font-bold">{m.name || "—"}</td>
                <td className="text-slate-600">{m.email}</td>
                <td className="font-mono text-xs">{m.accepted_version || "—"}</td>
                <td className="text-slate-600 text-xs">{m.accepted_at ? new Date(m.accepted_at).toLocaleString("it-IT") : "—"}</td>
                <td>{m.up_to_date ? <span className="text-emerald-700 text-xs font-bold">aggiornato</span> : <span className="text-amber-700 text-xs font-bold">da accettare</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
